import { useCallback, useEffect, useRef, useState } from "react";
import ReactEcharts from "echarts-for-react";
import { EChartsOption } from "echarts-for-react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

import { xSymbolUnicode } from "../../app/utils";
import { useAppSelector } from "../../app/hooks";
import { isContrastMode } from "../../app/contrastSlice";
import { GetTraitFeedback, GetTraitLlmFeedback } from "../../app/writingAnalysis";
import { Popover, PopoverContent, PopoverHeading, PopoverTrigger } from "../../components/Popover";
import { TraitFeedback } from "../writing/drafting/TraitFeedback";
import { TraitLlmFeedback } from "../writing/drafting/TraitLlmFeedback";
import { TraitScore } from "../writing/drafting/TraitScore";
import { TraitTutorials } from "../writing/drafting/TraitTutorials";
import { miwChartColors, utahChartColors } from "../reports/shared/reportUtil";
import {
	DraftScoreViewModel,
	ScoredCategoryViewModel,
	WritingAnalysisViewModel,
} from "./scoreReportApiSlice";

const miwHighlightColor = "#ffba00"; // tailwind amber 400
const utahHighlightColor = "#cb4b27";

export interface Props {
	analysis: WritingAnalysisViewModel;
	draftScore: DraftScoreViewModel;
	isConstructedResponse: boolean;
	className?: string;
}

export default function ScoreReportChart(props: Props) {
	const { config } = useAppSelector((state) => state.site);
	const [open, setOpen] = useState(false);
	const [currentCategory, setCurrentCategory] = useState<ScoredCategoryViewModel>();
	const [currentIndex, setCurrentIndex] = useState<number>();

	const mostRecentIndexRef = useRef<number>(0);
	const skipSecondCallRef = useRef<boolean>(false);

	useEffect(() => {
		if (currentIndex != undefined) {
			const category = props.analysis.categories[currentIndex];
			setCurrentCategory(category);

			mostRecentIndexRef.current = currentIndex;
			if (skipSecondCallRef.current) {
				setOpen(true);
			}
		}
	}, [currentIndex]);

	function handleChartClick() {
		if (!skipSecondCallRef.current) {
			setOpen(!open);
		}

		if (!currentIndex) {
			setCurrentIndex(0);
		}

		skipSecondCallRef.current = false;
	}

	const handleBarClick = useCallback((event?: { dataIndex: any }) => {
		// this function is memoized to enable smooth transition between bar clicks and avoid jumpiness in the chart
		setCurrentIndex(event?.dataIndex);

		skipSecondCallRef.current = mostRecentIndexRef.current === event?.dataIndex ? false : true;
	}, []);

	function handlePrevious() {
		setCurrentIndex((prevValue) => (prevValue ? prevValue - 1 : prevValue));
	}

	function handleNext() {
		setCurrentIndex((prevValue) => (prevValue != undefined ? prevValue + 1 : prevValue));
	}

	function handleXClick() {
		setOpen(false);
	}

	const feedback = GetTraitFeedback(props.analysis, currentCategory);
	const llmFeedback = GetTraitLlmFeedback(props.analysis, currentCategory);

	return (
		<div className="w-full h-full">
			<Popover open={open} onOpenChange={setOpen} placement="top-start">
				<PopoverTrigger onClick={handleChartClick}>
					<div className="h-full xl:w-[1050px] lg:w-[800px] md:w-[600px]">
						<Chart
							draftScore={props.draftScore}
							isConstructedResponse={props.isConstructedResponse}
							currentIndex={currentIndex}
							open={open}
							onClick={handleBarClick}
						/>
					</div>
				</PopoverTrigger>
				<div>
					<PopoverContent className="bg-white rounded-md border border-gray-200 shadow-md shadow-gray-200">
						<PopoverHeading className="flex justify-between py-2 px-4 bg-gray-100 border border-b-gray-200">
							<div className="text-base font-bold text-primary-700">
								{config?.isUtahComposeSite ? "Writing Element" : "Trait"} Analysis
							</div>
							<div className="cursor-pointer" onClick={handleXClick}>
								{xSymbolUnicode}
							</div>
						</PopoverHeading>
						{currentCategory && currentIndex != undefined ? (
							<section key={currentCategory.id}>
								<div className="max-h-[420px] w-[475px] overflow-y-scroll overflow-x-auto p-4">
									<div className="flex justify-between">
										<h2>{props.analysis.categories[currentIndex].name}</h2>
										{!props.isConstructedResponse ? (
											<span className="isolate inline-flex rounded-md shadow-sm">
												<button
													onClick={handlePrevious}
													disabled={currentIndex <= 0}
													type="button"
													className="btn btn-primary relative inline-flex items-center rounded-l-md bg-white !px-2 !py-2 text-gray-400 ring-1 ring-inset ring-gray-300 focus:z-10">
													<span className="sr-only">Previous</span>
													<ChevronLeftIcon
														className="h-5 w-5"
														aria-hidden="true"
													/>
												</button>
												<button
													onClick={handleNext}
													disabled={currentIndex >= 5}
													type="button"
													className="btn btn-primary relative -ml-px inline-flex items-center rounded-r-md bg-white !px-2 !py-2 text-gray-400 ring-1 ring-inset ring-gray-300 focus:z-10">
													<span className="sr-only">Next</span>
													<ChevronRightIcon
														className="h-5 w-5"
														aria-hidden="true"
													/>
												</button>
											</span>
										) : (
											<></>
										)}
									</div>

									<TraitScore
										score={props.analysis.categories[currentIndex].score!}
										maxScore={props.analysis.categories[currentIndex].maxScore!}
										visible={props.analysis.showScore}
										oneline={false}
									/>
									<TraitLlmFeedback
										className="mt-4"
										heading="Writing Advisor"
										traitId={currentCategory.id}
										llmFeedbackId={props.analysis.llmFeedback?.id}
										llmFeedback={llmFeedback}
										showCommentButton={false}
									/>
									<TraitFeedback
										className="mt-4"
										heading="Evaluation and Feedback"
										feedback={feedback}
										isProctorScored={currentCategory.isProctorScored}
									/>
									<TraitTutorials
										className="mt-4"
										heading="Lessons"
										tutorials={
											props.analysis.feedbackTutorials[currentCategory.id]
										}
									/>
								</div>
							</section>
						) : (
							<></>
						)}
					</PopoverContent>
				</div>
			</Popover>
		</div>
	);
}

interface ChartProps {
	onClick: (e: any) => void;
	draftScore: DraftScoreViewModel;
	isConstructedResponse: boolean;
	currentIndex: number | undefined;
	open: boolean;
}

function Chart(props: ChartProps) {
	const echartsRef = useRef<any>(null);
	const { config } = useAppSelector((state) => state.site);
	const onEvents = { click: props.onClick };
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;

	let { backgroundColor, foregroundColor, accentColor, chartPalette } = useAppSelector(
		(state) => state.contrast
	);

	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	}

	const highlightColor = config?.isUtahComposeSite ? utahHighlightColor : miwHighlightColor;

	const names = props.draftScore.categoryScores
		.filter((item) => !item.isProctorScored)
		.map((item) => item.name);
	const scores = props.draftScore.categoryScores
		.filter((item) => !item.isProctorScored)
		.map((item) => item.score);

	let seriesData = scores.map((score, index) => {
		return {
			value: score,
			itemStyle: {
				color:
					props.open && index === props.currentIndex
						? highlightColor
						: chartColors[index % chartColors.length],
			},
		};
	});

	const option: EChartsOption = {
		title: {
			text: `${config?.isUtahComposeSite ? "Analytical" : "Trait"} Scores`,
			padding: [0, 10],
			top: 20,
			left: 20,
		},
		xAxis: {
			type: "category",
			data: names,
			axisLabel: {
				interval: 0,
				width: 85,
				overflow: "break",
				fontWeight: "bold",
			},
		},
		yAxis: {
			type: "value",
			max: props.isConstructedResponse ? 3 : 5,
			axisLabel: {
				fontWeight: "bold",
			},
		},
		series: [
			{
				data: seriesData,
				type: "bar",
				label: {
					show: true,
					position: ["50%", "0%"],
					height: 30,
					width: 30,
					align: "center",
					verticalAlign: "middle",
					padding: 4,
					backgroundColor: "#fff",
					borderRadius: 30,
					borderColor: "inherit",
					borderWidth: 1,
					fontWeight: "bolder",
					fontSize: 14,
				},
			},
		],
	};

	const chartStyles: EChartsOption = {
		title: {
			textStyle: {
				color: foregroundColor,
			},
		},
		xAxis: {
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		yAxis: {
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
	};

	useEffect(() => {
		const chart = echartsRef.current?.getEchartsInstance();
		if (!chart) return;
		// if not in contrast mode or we are using white on black, dont change chart colors on print.
		chart.setOption(chartStyles);

		const handleBeforePrint: EChartsOption = () => {
			chart.setOption({
				title: {
					textStyle: {
						color: "#000",
					},
				},
				xAxis: {
					axisLine: { lineStyle: { color: "#000" } },
					axisLabel: { color: "#000" },
				},
				yAxis: {
					axisLine: { lineStyle: { color: "#000" } },
					axisLabel: { color: "#000" },
					splitLine: {
						show: true,
						lineStyle: {
							color: isContrastMode() ? "#000" : accentColor,
						},
					},
				},
			});
		};

		const handleAfterPrint = () => {
			chart.setOption(chartStyles);
		};

		window.addEventListener("beforeprint", handleBeforePrint);
		window.addEventListener("afterprint", handleAfterPrint);

		return () => {
			window.removeEventListener("beforeprint", handleBeforePrint);
			window.removeEventListener("afterprint", handleAfterPrint);
		};
	}, [backgroundColor]);

	return (
		<ReactEcharts
			ref={echartsRef}
			option={option}
			className="w-full h-full"
			onEvents={onEvents}
		/>
	);
}
